<template>
  <div class="wallet-page">

    <!-- ═══ Low Balance Banner ═══ -->
    <div v-if="showLowBalanceBanner" class="low-balance-bar">
      <i class="fas fa-exclamation-triangle"></i>
      <div>
        <strong>Your balance is running low.</strong>
        You have <strong>{{ balance }}</strong> credits remaining — enough for approximately <strong>{{ balance }}</strong> standard messages.
      </div>
      <a href="sms-buy-credits.html" class="btn-warning-sm" style="margin-left:auto;white-space:nowrap;flex-shrink:0;">
        Buy Credits Now
      </a>
    </div>


    <!-- ═══ Page Header ═══ -->
    <div class="page-header">
      <div>
        <nav class="breadcrumb-nav">
          <span>SMS</span>
          <span class="sep">/</span>
          <span class="active">Wallet &amp; Credits</span>
        </nav>
        <h4 class="page-title">Wallet &amp; Credits</h4>
      </div>
      <div class="header-actions">
        <button class="btn-outline" @click="exportCSV">
          <i class="fas fa-download"></i> Export Statement
        </button>
        <a href="sms-buy-credits.html" class="btn-primary-sm">
          <i class="fas fa-plus"></i> Buy Credits
        </a>
      </div>
    </div>

    <!-- ═══ Hero Balance Card ═══ -->
    <div class="wallet-hero">
      <div class="hero-inner">
        <!-- Main balance -->
        <div class="hero-main">
          <div class="hero-label">Available Balance</div>
          <div class="hero-balance">{{ balance.toLocaleString() }}</div>
          <div class="hero-sub">SMS credits</div>
          <div class="usage-block">
            <div class="usage-top">
              <span>Used this period</span>
              <span class="usage-pct">{{ usagePct }}%</span>
            </div>
            <div class="usage-track">
              <div class="usage-fill" :style="{ width: usagePct + '%' }"></div>
            </div>
            <div class="usage-bottom">
              <span>{{ totalUsed.toLocaleString() }} used</span>
              <span>{{ totalPurchased.toLocaleString() }} purchased</span>
            </div>
          </div>
        </div>

        <div class="hero-divider"></div>

        <div class="hero-stat">
          <div class="hero-stat-lbl">Total Purchased</div>
          <div class="hero-stat-val">{{ totalPurchased.toLocaleString() }}</div>
          <div class="hero-stat-sub">all time</div>
        </div>

        <div class="hero-divider"></div>

        <div class="hero-stat">
          <div class="hero-stat-lbl">Total Used</div>
          <div class="hero-stat-val">{{ totalUsed.toLocaleString() }}</div>
          <div class="hero-stat-sub">all time</div>
        </div>

        <div class="hero-divider"></div>

        <div class="hero-stat">
          <div class="hero-stat-lbl">This Month</div>
          <div class="hero-stat-val">312</div>
          <div class="hero-stat-sub">messages sent</div>
        </div>

        <div class="hero-cta">
          <a href="sms-buy-credits.html" class="btn-light-block">
            <i class="fas fa-plus"></i> Buy More Credits
          </a>
          <div class="hero-cta-sub">Credits never expire</div>
        </div>
      </div>
    </div>

    <!-- ═══ Stat Mini Cards ═══ -->
    <div class="stat-row">
      <div class="stat-mini">
        <div class="stat-icon" style="background:#eef3ff;">
          <i class="fas fa-arrow-down" style="color:#0168fa;"></i>
        </div>
        <div>
          <div class="stat-val">£89.97</div>
          <div class="stat-lbl">Total Spent</div>
          <div class="stat-trend trend-up"><i class="fas fa-arrow-up"></i> 12% vs last month</div>
        </div>
      </div>
      <div class="stat-mini">
        <div class="stat-icon" style="background:#e6f9f2;">
          <i class="fas fa-paper-plane" style="color:#10b981;"></i>
        </div>
        <div>
          <div class="stat-val">{{ totalUsed }}</div>
          <div class="stat-lbl">Credits Used</div>
          <div class="stat-trend trend-up"><i class="fas fa-arrow-up"></i> 8% vs last month</div>
        </div>
      </div>
      <div class="stat-mini">
        <div class="stat-icon" style="background:#fef2f2;">
          <i class="fas fa-undo" style="color:#ef4444;"></i>
        </div>
        <div>
          <div class="stat-val">12</div>
          <div class="stat-lbl">Refunds</div>
          <div class="stat-trend trend-down"><i class="fas fa-arrow-down"></i> 3 vs last month</div>
        </div>
      </div>
      <div class="stat-mini">
        <div class="stat-icon" style="background:#fefce8;">
          <i class="fas fa-coins" style="color:#f59e0b;"></i>
        </div>
        <div>
          <div class="stat-val">£0.040</div>
          <div class="stat-lbl">Avg Cost / SMS</div>
          <div class="stat-trend trend-down"><i class="fas fa-arrow-down"></i> savings vs Starter</div>
        </div>
      </div>
    </div>

    <!-- ═══ Main Content Row ═══ -->
    <div class="main-grid">

      <!-- LEFT: Transaction Table -->
      <div class="col-left">
        <!-- Filter bar -->
        <div class="card-header-bar">
          <h6 class="card-title">Transaction History</h6>
          <div class="filter-bar">
            <select v-model="filterType" @change="applyFilters" class="filter-select">
              <option value="">All types</option>
              <option value="PURCHASE">Purchase</option>
              <option value="SMS_SEND">SMS Send</option>
              <option value="REFUND">Refund</option>
              <option value="ADJUSTMENT">Adjustment</option>
            </select>
            <select v-model="filterMonth" @change="applyFilters" class="filter-select">
              <option value="">All time</option>
              <option value="Apr 2026">Apr 2026</option>
              <option value="Mar 2026">Mar 2026</option>
              <option value="Feb 2026">Feb 2026</option>
            </select>
            <button class="btn-icon" @click="exportCSV" title="Export">
              <i class="fas fa-download"></i>
            </button>
          </div>
        </div>

        <!-- Table -->
        <div class="card table-card">
          <div class="table-wrap">
            <table v-if="paginatedTxns.length" class="txn-table">
              <thead>
              <tr>
                <th>Date &amp; Time</th>
                <th>Type</th>
                <th>Description</th>
                <th class="text-center">Credits</th>
                <th class="text-right">Balance</th>
                <th style="width:36px;"></th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="t in paginatedTxns" :key="t.id">
                <td class="date-cell">
                  {{ formatDate(t.date) }}
                  <span class="time-part">{{ formatTime(t.date) }}</span>
                </td>
                <td>
                  <span class="txn-type" :class="typeClass(t.type)">{{ typeLabel(t.type) }}</span>
                </td>
                <td>
                  <div class="txn-desc">{{ t.desc }}</div>
                  <div class="txn-ref">{{ t.ref }}</div>
                </td>
                <td class="text-center">
                    <span class="delta" :class="t.delta > 0 ? 'pos' : 'neg'">
                      {{ t.delta > 0 ? '+' : '' }}{{ t.delta.toLocaleString() }}
                    </span>
                </td>
                <td class="text-right txn-balance">{{ t.balance.toLocaleString() }}</td>
                <td>
                  <button v-if="t.type === 'PURCHASE'" class="btn-receipt" title="View receipt">
                    <i class="fas fa-receipt"></i>
                  </button>
                </td>
              </tr>
              </tbody>
            </table>

            <!-- Empty state -->
            <div v-else class="empty-state">
              <i class="fas fa-receipt"></i>
              <p>No transactions match your filters.</p>
              <button class="btn-outline-sm" @click="clearFilters">Clear filters</button>
            </div>
          </div>

          <!-- Pagination -->
          <div class="card-footer-bar">
            <span class="pag-label">{{ pagLabel }}</span>
            <nav v-if="totalPages > 1" class="pagination">
              <button class="page-btn" :disabled="currentPage === 1" @click="goPage(currentPage - 1)">
                <i class="fas fa-angle-left"></i>
              </button>
              <button
                  v-for="p in totalPages"
                  :key="p"
                  class="page-btn"
                  :class="{ active: p === currentPage }"
                  @click="goPage(p)"
              >{{ p }}</button>
              <button class="page-btn" :disabled="currentPage === totalPages" @click="goPage(currentPage + 1)">
                <i class="fas fa-angle-right"></i>
              </button>
            </nav>
          </div>
        </div>
      </div>
      <!-- /LEFT -->

      <!-- RIGHT: Charts + packages -->
      <div class="col-right">

        <!-- Donut Chart -->
        <div class="card mg-b">
          <div class="card-header-bar">
            <h6 class="card-title">Credit Breakdown</h6>
            <select class="filter-select-sm">
              <option>This month</option>
              <option>Last 30 days</option>
              <option>All time</option>
            </select>
          </div>
          <div class="card-body">
            <div class="donut-wrap">
              <canvas ref="donutChartEl" width="160" height="160"></canvas>
              <div class="donut-center">
                <div class="dc-val">{{ totalUsed }}</div>
                <div class="dc-lbl">used</div>
              </div>
            </div>
            <div class="donut-legend">
              <div class="legend-row" v-for="item in legendItems" :key="item.label">
                <div class="legend-left">
                  <span class="legend-dot" :style="{ background: item.color }"></span>
                  <span>{{ item.label }}</span>
                </div>
                <span class="legend-val">{{ item.val }} <span class="legend-pct">({{ item.pct }})</span></span>
              </div>
              <div class="legend-row legend-footer">
                <span>Remaining</span>
                <span class="remaining-val">{{ balance.toLocaleString() }} credits</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Monthly Bar Chart -->
        <div class="card mg-b">
          <div class="card-header-bar">
            <h6 class="card-title">Monthly Usage</h6>
          </div>
          <div class="card-body">
            <canvas ref="barChartEl" height="140"></canvas>
          </div>
        </div>

        <!-- Top Up Packages -->
        <div class="card">
          <div class="card-header-bar">
            <h6 class="card-title">Top Up Credits</h6>
            <a href="sms-buy-credits.html" class="card-link">View all →</a>
          </div>
          <div class="card-body pkg-list">
            <div
                v-for="pkg in packages"
                :key="pkg.name"
                class="pkg-row"
                :class="{ 'pkg-featured': pkg.popular }"
                @click="quickBuy(pkg)"
            >
              <div class="pkg-icon" :style="{ background: pkg.iconBg }">
                <i :class="pkg.icon" :style="{ color: pkg.iconColor }"></i>
              </div>
              <div class="pkg-info">
                <div class="pkg-name">
                  {{ pkg.name }}
                  <span v-if="pkg.popular" class="badge-popular">Popular</span>
                </div>
                <div class="pkg-sub">{{ pkg.credits.toLocaleString() }} credits · £{{ pkg.perSms }}/SMS</div>
              </div>
              <span class="pkg-price">£{{ pkg.price.toFixed(2) }}</span>
            </div>
          </div>
        </div>

      </div>
      <!-- /RIGHT -->
    </div>

    <!-- ═══ Quick Buy Modal ═══ -->
    <Teleport to="body">
      <div v-if="showModal" class="modal-backdrop" @click.self="showModal = false">
        <div class="modal-dialog">
          <div class="modal-header">
            <h6>Confirm Purchase</h6>
            <button class="modal-close" @click="showModal = false">&times;</button>
          </div>
          <div class="modal-body">
            <div class="modal-icon-wrap">
              <i class="fas fa-coins" style="color:#0168fa;font-size:22px;"></i>
            </div>
            <div class="modal-credits">{{ pendingPkg?.credits.toLocaleString() }}</div>
            <div class="modal-credits-sub">credits will be added to your wallet</div>
            <table class="modal-table">
              <tr>
                <td>Package</td>
                <td>{{ pendingPkg?.name }} — {{ pendingPkg?.credits.toLocaleString() }} credits</td>
              </tr>
              <tr>
                <td>Amount</td>
                <td class="price-cell">£{{ pendingPkg?.price.toFixed(2) }}</td>
              </tr>
              <tr class="modal-table-divider">
                <td>New balance</td>
                <td class="newbal-cell">{{ pendingPkg ? (balance + pendingPkg.credits).toLocaleString() : '' }} credits</td>
              </tr>
            </table>
          </div>
          <div class="modal-footer">
            <button class="btn-outline" @click="showModal = false">Cancel</button>
            <button class="btn-primary-sm" :disabled="processingBuy" @click="confirmQuickBuy">
              <span v-if="processingBuy">
                <i class="fas fa-spinner fa-spin"></i> Processing…
              </span>
              <span v-else>
                <i class="fas fa-lock"></i> Pay &amp; Add Credits
              </span>
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ═══ Toast ═══ -->
    <Teleport to="body">
      <Transition name="toast-fade">
        <div v-if="toast.show" class="toast-wrap" :class="toast.error ? 'toast-error' : 'toast-success'">
          <i :class="toast.error ? 'fas fa-exclamation-circle' : 'fas fa-check-circle'" class="toast-icon"></i>
          <div class="toast-text">
            <div class="toast-title">{{ toast.title }}</div>
            <div class="toast-msg">{{ toast.msg }}</div>
          </div>
          <button class="toast-close" @click="toast.show = false">&times;</button>
        </div>
      </Transition>
    </Teleport>

  </div>
</template>

<script>
// If using a bundler: import Chart from 'chart.js/auto'
// For CDN usage the global window.Chart is used as fallback below.

const PAGE_SIZE = 10

const TYPE_CLASSES = {
  PURCHASE:   'type-purchase',
  SMS_SEND:   'type-sms_send',
  REFUND:     'type-refund',
  ADJUSTMENT: 'type-adjustment',
}

const TYPE_LABELS = {
  PURCHASE:   'Purchase',
  SMS_SEND:   'SMS Send',
  REFUND:     'Refund',
  ADJUSTMENT: 'Adjustment',
}

export default {
  name: 'SmsWallet',

  /* ─── Reactive data ─── */
  data() {
    return {
      // Wallet totals
      balance:         950,
      totalPurchased:  1000,
      totalUsed:       550,

      // Filters & pagination
      filterType:  '',
      filterMonth: 'Apr 2026',
      currentPage: 1,

      // Modal
      showModal:    false,
      processingBuy: false,
      pendingPkg:   null,

      // Toast
      toast: { show: false, title: '', msg: '', error: false },

      // Static lookup data
      packages: [
        { name: 'Starter',    credits: 100,   price: 4.99,   perSms: '0.050', popular: false, icon: 'fas fa-bolt',     iconBg: '#f4f5f8', iconColor: '#8392a5' },
        { name: 'Growth',     credits: 500,   price: 19.99,  perSms: '0.040', popular: true,  icon: 'fas fa-star',     iconBg: '#eef3ff', iconColor: '#0168fa' },
        { name: 'Pro',        credits: 2000,  price: 64.99,  perSms: '0.032', popular: false, icon: 'fas fa-rocket',   iconBg: '#f0faf5', iconColor: '#10b981' },
        { name: 'Enterprise', credits: 10000, price: 249.99, perSms: '0.025', popular: false, icon: 'fas fa-building', iconBg: '#fff7ed', iconColor: '#f97316' },
      ],

      legendItems: [
        { label: 'Campaigns',    val: 340, pct: '62%', color: '#0168fa' },
        { label: 'Manual sends', val: 198, pct: '36%', color: '#10b981' },
        { label: 'Refunded',     val: 12,  pct: '2%',  color: '#f59e0b' },
      ],

      // Transaction list
      allTxns: [
        { id: 'TXN-048', date: new Date('2026-04-03T10:22:00'), type: 'PURCHASE',   desc: 'Growth pack — 500 credits',              delta: +500,  balance: 950,  ref: 'pi_3abc001' },
        { id: 'TXN-047', date: new Date('2026-04-03T10:15:00'), type: 'SMS_SEND',   desc: 'Send batch — 3 messages',               delta: -3,    balance: 450,  ref: 'batch-001' },
        { id: 'TXN-046', date: new Date('2026-04-03T09:04:00'), type: 'REFUND',     desc: 'Failed delivery refund (+447700900003)', delta: +1,    balance: 453,  ref: 'msg-003' },
        { id: 'TXN-045', date: new Date('2026-04-02T14:30:00'), type: 'SMS_SEND',   desc: 'Campaign: April promo — 840 messages',  delta: -840,  balance: 452,  ref: 'camp-001' },
        { id: 'TXN-044', date: new Date('2026-04-01T08:00:00'), type: 'PURCHASE',   desc: 'Pro pack — 2,000 credits',              delta: +2000, balance: 1292, ref: 'pi_3abc002' },
        { id: 'TXN-043', date: new Date('2026-03-28T16:10:00'), type: 'SMS_SEND',   desc: 'Send batch — 12 messages',              delta: -12,   balance: 708,  ref: 'batch-002' },
        { id: 'TXN-042', date: new Date('2026-03-25T11:45:00'), type: 'REFUND',     desc: 'Failed delivery refund (+447700900008)',delta: +1,    balance: 720,  ref: 'msg-018' },
        { id: 'TXN-041', date: new Date('2026-03-20T09:00:00'), type: 'SMS_SEND',   desc: 'Campaign: March newsletter — 620 msgs', delta: -620,  balance: 719,  ref: 'camp-002' },
        { id: 'TXN-040', date: new Date('2026-03-15T13:20:00'), type: 'PURCHASE',   desc: 'Growth pack — 500 credits',             delta: +500,  balance: 1339, ref: 'pi_3abc003' },
        { id: 'TXN-039', date: new Date('2026-03-10T09:05:00'), type: 'SMS_SEND',   desc: 'Send batch — 24 messages',              delta: -24,   balance: 839,  ref: 'batch-003' },
        { id: 'TXN-038', date: new Date('2026-03-08T15:00:00'), type: 'REFUND',     desc: 'Failed delivery refund (×3)',           delta: +3,    balance: 863,  ref: 'batch-003' },
        { id: 'TXN-037', date: new Date('2026-03-01T08:00:00'), type: 'SMS_SEND',   desc: 'Campaign: Feb flash sale — 312 msgs',   delta: -312,  balance: 860,  ref: 'camp-003' },
        { id: 'TXN-036', date: new Date('2026-02-28T10:30:00'), type: 'PURCHASE',   desc: 'Starter pack — 100 credits',            delta: +100,  balance: 1172, ref: 'pi_3abc004' },
        { id: 'TXN-035', date: new Date('2026-02-20T14:00:00'), type: 'SMS_SEND',   desc: 'Send batch — 8 messages',               delta: -8,    balance: 1072, ref: 'batch-004' },
        { id: 'TXN-034', date: new Date('2026-02-14T12:00:00'), type: 'ADJUSTMENT', desc: 'Manual credit adjustment',              delta: +50,   balance: 1080, ref: 'ADJ-001' },
        { id: 'TXN-033', date: new Date('2026-02-10T09:00:00'), type: 'SMS_SEND',   desc: 'Send batch — 6 messages',               delta: -6,    balance: 1030, ref: 'batch-005' },
      ],
    }
  },

  /* ─── Computed properties ─── */
  computed: {
    usagePct() {
      return Math.round((this.totalUsed / this.totalPurchased) * 100)
    },

    showLowBalanceBanner() {
      return this.balance < 100
    },

    filteredTxns() {
      return this.allTxns.filter(t => {
        if (this.filterType && t.type !== this.filterType) return false
        if (this.filterMonth) {
          const mo = t.date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
          if (mo !== this.filterMonth) return false
        }
        return true
      })
    },

    totalPages() {
      return Math.ceil(this.filteredTxns.length / PAGE_SIZE)
    },

    paginatedTxns() {
      const start = (this.currentPage - 1) * PAGE_SIZE
      return this.filteredTxns.slice(start, start + PAGE_SIZE)
    },

    pagLabel() {
      if (!this.filteredTxns.length) return 'No results'
      const start = (this.currentPage - 1) * PAGE_SIZE + 1
      const end   = Math.min(this.currentPage * PAGE_SIZE, this.filteredTxns.length)
      return `Showing ${start}–${end} of ${this.filteredTxns.length}`
    },
  },

  /* ─── Watchers ─── */
  watch: {
    // Reset to page 1 whenever the filtered set changes
    filteredTxns() {
      this.currentPage = 1
    },
  },

  /* ─── Lifecycle ─── */
  created() {
    // Attach chart refs directly on the instance so they are never
    // made reactive by Vue (avoids vue/no-reserved-keys and saves overhead)
    this.donutInstance = null
    this.barInstance   = null
    this.chartLib      = null
  },

  async mounted() {
    try {
      const { Chart, ArcElement, BarElement, CategoryScale, LinearScale,
        DoughnutController, BarController, Tooltip, Legend } = await import('chart.js')
      Chart.register(ArcElement, BarElement, CategoryScale, LinearScale,
          DoughnutController, BarController, Tooltip, Legend)
      this.chartLib = Chart
    } catch {
      this.chartLib = window.Chart
    }
    this.initCharts()
  },

  beforeUnmount() {
    if (this.donutInstance) this.donutInstance.destroy()
    if (this.barInstance)   this.barInstance.destroy()
  },

  /* ─── Methods ─── */
  methods: {
    /* Filters & pagination */
    applyFilters() {
      this.currentPage = 1
    },

    clearFilters() {
      this.filterType  = ''
      this.filterMonth = ''
    },

    goPage(p) {
      if (p < 1 || p > this.totalPages) return
      this.currentPage = p
    },

    /* Type helpers */
    typeClass(type) {
      return TYPE_CLASSES[type] || ''
    },

    typeLabel(type) {
      return TYPE_LABELS[type] || type
    },

    /* Date helpers */
    formatDate(date) {
      return `${date.getDate()} ${date.toLocaleString('en-GB', { month: 'short' })} ${date.getFullYear()}`
    },

    formatTime(date) {
      return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
    },

    /* Quick-buy modal */
    quickBuy(pkg) {
      this.pendingPkg = pkg
      this.showModal  = true
    },

    confirmQuickBuy() {
      if (!this.pendingPkg) return
      this.processingBuy = true

      setTimeout(() => {
        const pkg = this.pendingPkg

        this.balance        += pkg.credits
        this.totalPurchased += pkg.credits

        this.allTxns.unshift({
          id:      'TXN-' + String(this.allTxns.length + 49).padStart(3, '0'),
          date:    new Date(),
          type:    'PURCHASE',
          desc:    `${pkg.name} pack — ${pkg.credits.toLocaleString()} credits`,
          delta:   +pkg.credits,
          balance: this.balance,
          ref:     'pi_' + Math.random().toString(36).substring(2, 10),
        })

        this.showModal     = false
        this.processingBuy = false
        this.pendingPkg    = null

        this.showToast(
            'Credits Added!',
            `${pkg.credits.toLocaleString()} credits added. New balance: ${this.balance.toLocaleString()}`
        )
      }, 1500)
    },

    /* Toast */
    showToast(title, msg, error = false) {
      this.toast = { show: true, title, msg, error }
      setTimeout(() => { this.toast.show = false }, 5000)
    },

    /* CSV export */
    exportCSV() {
      const rows = [['ID', 'Date', 'Type', 'Description', 'Delta', 'Balance', 'Ref']]
      this.filteredTxns.forEach(t => {
        rows.push([t.id, t.date.toISOString(), t.type, `"${t.desc}"`, t.delta, t.balance, t.ref])
      })
      const csv = rows.map(r => r.join(',')).join('\n')
      const a   = document.createElement('a')
      a.href     = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv)
      a.download = 'wallet-statement-' + new Date().toISOString().slice(0, 10) + '.csv'
      a.click()
      this.showToast('Exported', `${this.filteredTxns.length} transactions exported to CSV.`)
    },

    /* Charts */
    initCharts() {
      const C = this.chartLib
      if (!C) return

      // ── Donut ──
      const donutEl = this.$refs.donutChartEl
      if (donutEl) {
        this.donutInstance = new C(donutEl.getContext('2d'), {
          type: 'doughnut',
          data: {
            labels: ['Campaigns', 'Manual', 'Refunded', 'Remaining'],
            datasets: [{
              data: [340, 198, 12, 950],
              backgroundColor: ['#0168fa', '#10b981', '#f59e0b', '#e5e9f2'],
              borderWidth: 0,
              hoverOffset: 4,
            }],
          },
          options: {
            cutout: '72%',
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: ctx => ` ${ctx.label}: ${ctx.parsed} credits`,
                },
              },
            },
            animation: { animateRotate: true, duration: 800 },
          },
        })
      }

      // ── Bar ──
      const barEl = this.$refs.barChartEl
      if (barEl) {
        this.barInstance = new C(barEl.getContext('2d'), {
          type: 'bar',
          data: {
            labels: ['Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr'],
            datasets: [
              { label: 'Used',      data: [210, 180, 320, 490, 656, 553], backgroundColor: '#0168fa', borderRadius: 4, barPercentage: 0.6 },
              { label: 'Purchased', data: [500, 0,   500, 600, 500, 500], backgroundColor: '#e5e9f2', borderRadius: 4, barPercentage: 0.6 },
            ],
          },
          options: {
            responsive: true,
            plugins: { legend: { display: false } },
            scales: {
              x: { grid: { display: false }, ticks: { font: { size: 11 }, color: '#8392a5' } },
              y: { grid: { color: '#f0f2f7' }, ticks: { font: { size: 11 }, color: '#8392a5', maxTicksLimit: 5 } },
            },
          },
        })
      }
    },
  },
}
</script>

<style scoped>
/* ═══ Reset / Base ═══ */
.wallet-page {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  color: #1c273c;
  background: #f4f5f8;
  min-height: 100vh;
}

/* ═══ Low Balance Banner ═══ */
.low-balance-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 12px 18px;
  margin-bottom: 20px;
  font-size: 13px;
  color: #92400e;
}

/* ═══ Page Header ═══ */
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
}
.breadcrumb-nav {
  font-size: 12px;
  color: #8392a5;
  margin-bottom: 6px;
}
.breadcrumb-nav .sep { margin: 0 6px; }
.breadcrumb-nav .active { color: #596882; font-weight: 500; }
.page-title {
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.3px;
}
.header-actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }

/* ═══ Buttons ═══ */
.btn-outline {
  background: #fff;
  border: 1px solid #d5d9e3;
  border-radius: 6px;
  padding: 7px 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  color: #596882;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  transition: border-color .15s, color .15s;
}
.btn-outline:hover { border-color: #0168fa; color: #0168fa; }
.btn-outline-sm {
  background: #fff;
  border: 1px solid #d5d9e3;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  color: #596882;
}
.btn-primary-sm {
  background: #0168fa;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 7px 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  transition: background .15s;
}
.btn-primary-sm:hover { background: #0148c0; }
.btn-primary-sm:disabled { opacity: .7; cursor: not-allowed; }
.btn-warning-sm {
  background: #f59e0b;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
}
.btn-icon {
  background: #fff;
  border: 1px solid #d5d9e3;
  border-radius: 6px;
  padding: 0 10px;
  height: 34px;
  cursor: pointer;
  color: #596882;
  font-size: 12px;
}
.btn-icon:hover { border-color: #0168fa; color: #0168fa; }
.btn-receipt {
  background: #fff;
  border: 1px solid #e5e9f2;
  border-radius: 4px;
  padding: 3px 7px;
  cursor: pointer;
  color: #596882;
  font-size: 11px;
}
.btn-light-block {
  display: block;
  background: rgba(255,255,255,.18);
  border: 1px solid rgba(255,255,255,.3);
  color: #fff;
  border-radius: 6px;
  padding: 9px 18px;
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
  white-space: nowrap;
  transition: background .15s;
  backdrop-filter: blur(4px);
}
.btn-light-block:hover { background: rgba(255,255,255,.28); color: #fff; }

/* ═══ Hero Card ═══ */
.wallet-hero {
  background: linear-gradient(135deg, #0168fa 0%, #0148c0 100%);
  border-radius: 10px;
  padding: 28px 32px;
  color: #fff;
  position: relative;
  overflow: hidden;
  margin-bottom: 20px;
}
.wallet-hero::before {
  content: '';
  position: absolute;
  top: -60px; right: -60px;
  width: 240px; height: 240px;
  border-radius: 50%;
  background: rgba(255,255,255,.06);
}
.wallet-hero::after {
  content: '';
  position: absolute;
  bottom: -80px; right: 80px;
  width: 180px; height: 180px;
  border-radius: 50%;
  background: rgba(255,255,255,.04);
}
.hero-inner {
  display: flex;
  align-items: center;
  gap: 0;
  flex-wrap: wrap;
  position: relative;
  z-index: 1;
}
.hero-main { flex: 1; min-width: 180px; }
.hero-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: .08em;
  text-transform: uppercase;
  opacity: .75;
  margin-bottom: 6px;
}
.hero-balance {
  font-size: 52px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -2px;
  margin-bottom: 4px;
}
.hero-sub { font-size: 13px; opacity: .7; }
.hero-divider {
  width: 1px;
  background: rgba(255,255,255,.25);
  align-self: stretch;
  margin: 0 28px;
  display: none;
}
@media (min-width: 768px) { .hero-divider { display: block; } }

.hero-stat { margin-right: 28px; display: none; }
@media (min-width: 768px) { .hero-stat { display: block; } }
.hero-stat-lbl { font-size: 11px; opacity: .7; }
.hero-stat-val { font-size: 26px; font-weight: 700; line-height: 1.1; }
.hero-stat-sub { font-size: 11px; opacity: .65; margin-top: 4px; }

.hero-cta { display: none; margin-left: auto; position: relative; z-index: 1; }
@media (min-width: 992px) { .hero-cta { display: block; } }
.hero-cta-sub { font-size: 11px; opacity: .65; text-align: center; margin-top: 5px; }

/* Usage bar */
.usage-block { margin-top: 14px; max-width: 260px; }
.usage-top { display: flex; justify-content: space-between; margin-bottom: 4px; font-size: 11px; opacity: .7; }
.usage-pct { opacity: .9; font-weight: 600; }
.usage-track { height: 8px; background: rgba(255,255,255,.2); border-radius: 4px; overflow: hidden; }
.usage-fill {
  height: 100%;
  border-radius: 4px;
  background: rgba(255,255,255,.7);
  transition: width .6s ease;
}
.usage-bottom { display: flex; justify-content: space-between; margin-top: 4px; font-size: 11px; opacity: .6; }

/* ═══ Stat Mini Cards ═══ */
.stat-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 20px;
}
@media (min-width: 576px) { .stat-row { grid-template-columns: repeat(4, 1fr); } }

.stat-mini {
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e5e9f2;
  padding: 16px 18px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
}
.stat-icon {
  width: 40px; height: 40px;
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
}
.stat-val { font-size: 22px; font-weight: 700; color: #1c273c; line-height: 1.1; }
.stat-lbl { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .05em; color: #8392a5; margin-top: 2px; }
.stat-trend { font-size: 12px; margin-top: 4px; }
.stat-trend i { font-size: 9px; }
.trend-up   { color: #10b981; }
.trend-down { color: #ef4444; }

/* ═══ Main Grid ═══ */
.main-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
}
@media (min-width: 992px) {
  .main-grid { grid-template-columns: 1fr 340px; }
}
.col-left  { min-width: 0; }
.col-right { display: flex; flex-direction: column; gap: 20px; }

/* ═══ Cards ═══ */
.card {
  background: #fff;
  border: 1px solid #e5e9f2;
  border-radius: 8px;
  overflow: hidden;
}
.card.mg-b { margin-bottom: 0; /* handled by col-right gap */ }
.card-header-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid #e5e9f2;
  flex-wrap: wrap;
  gap: 8px;
}
.card-title { font-size: 13px; font-weight: 600; margin: 0; color: #1c273c; }
.card-link { font-size: 13px; color: #0168fa; text-decoration: none; }
.card-body { padding: 20px; }

/* ═══ Table Card ═══ */
.table-card {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-top: none;
}
.table-wrap { overflow-x: auto; }
.txn-table {
  width: 100%;
  border-collapse: collapse;
}
.txn-table thead th {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .05em;
  color: #8392a5;
  border-bottom: 1px solid #e5e9f2;
  padding: 10px 14px;
  white-space: nowrap;
}
.txn-table td {
  padding: 12px 14px;
  vertical-align: middle;
  border-top: 1px solid #f0f2f7;
  font-size: 13px;
  color: #1c273c;
}
.txn-table tbody tr:hover td { background: #f8f9fc; }
.text-center { text-align: center; }
.text-right  { text-align: right; }

.date-cell { font-size: 12px; color: #596882; white-space: nowrap; }
.time-part { color: #aab3c2; margin-left: 3px; }
.txn-desc { font-size: 13px; color: #1c273c; }
.txn-ref  { font-size: 11px; color: #aab3c2; font-family: monospace; margin-top: 1px; }
.txn-balance { font-weight: 600; font-size: 13px; }

/* Type badges */
.txn-type {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 20px;
  white-space: nowrap;
  letter-spacing: .02em;
}
.type-purchase   { background: #e6f9f2; color: #0d7a52; }
.type-sms_send   { background: #eef3ff; color: #2155b4; }
.type-refund     { background: #fefce8; color: #92400e; }
.type-adjustment { background: #f3f4f6; color: #374151; }

/* Delta */
.delta { font-size: 13px; font-weight: 700; white-space: nowrap; }
.delta.pos { color: #10b981; }
.delta.neg { color: #ef4444; }

/* Empty state */
.empty-state {
  padding: 40px;
  text-align: center;
  color: #8392a5;
}
.empty-state i { font-size: 32px; margin-bottom: 12px; opacity: .4; display: block; }
.empty-state p { font-size: 14px; margin-bottom: 10px; }

/* Pagination */
.card-footer-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-top: 1px solid #e5e9f2;
  flex-wrap: wrap;
  gap: 8px;
}
.pag-label { font-size: 13px; color: #8392a5; }
.pagination { display: flex; gap: 4px; list-style: none; margin: 0; padding: 0; }
.page-btn {
  min-width: 30px; height: 30px;
  border: 1px solid #e5e9f2;
  border-radius: 50%;
  background: #fff;
  font-size: 12px;
  cursor: pointer;
  color: #596882;
  display: flex; align-items: center; justify-content: center;
  transition: all .15s;
  padding: 0 6px;
}
.page-btn:hover:not(:disabled):not(.active) { border-color: #0168fa; color: #0168fa; }
.page-btn.active { background: #0168fa; border-color: #0168fa; color: #fff; font-weight: 700; }
.page-btn:disabled { opacity: .4; cursor: not-allowed; }

/* Filter bar */
.filter-bar { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.filter-select {
  border: 1px solid #d5d9e3;
  border-radius: 6px;
  padding: 0 10px;
  height: 34px;
  font-size: 13px;
  color: #1c273c;
  background: #fff;
  cursor: pointer;
}
.filter-select-sm {
  border: 1px solid #d5d9e3;
  border-radius: 6px;
  padding: 0 8px;
  height: 28px;
  font-size: 12px;
  background: #fff;
  cursor: pointer;
}

/* ═══ Donut ═══ */
.donut-wrap {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 160px;
}
.donut-center {
  position: absolute;
  text-align: center;
  pointer-events: none;
}
.dc-val { font-size: 22px; font-weight: 700; color: #1c273c; line-height: 1; }
.dc-lbl { font-size: 11px; color: #8392a5; margin-top: 2px; }
.donut-legend { margin-top: 16px; display: flex; flex-direction: column; gap: 8px; }
.legend-row { display: flex; align-items: center; justify-content: space-between; }
.legend-left { display: flex; align-items: center; gap: 7px; font-size: 13px; color: #596882; }
.legend-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; flex-shrink: 0; }
.legend-val { font-size: 13px; font-weight: 600; }
.legend-pct { color: #8392a5; font-weight: 400; }
.legend-footer {
  padding-top: 10px;
  border-top: 1px solid #f0f2f7;
  margin-top: 4px;
  font-size: 12px;
  color: #8392a5;
}
.remaining-val { font-size: 13px; font-weight: 700; color: #0168fa; }

/* ═══ Packages ═══ */
.pkg-list { padding: 0 20px 8px; }
.pkg-row {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f0f2f7;
  cursor: pointer;
  transition: background .1s;
  border-radius: 4px;
}
.pkg-row:last-child { border-bottom: none; }
.pkg-row:hover { background: #f8f9fc; }
.pkg-featured { background: #f5f9ff; }
.pkg-icon {
  width: 36px; height: 36px;
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  margin-right: 12px;
  font-size: 14px;
}
.pkg-info { flex: 1; }
.pkg-name { font-size: 13px; font-weight: 600; color: #1c273c; display: flex; align-items: center; gap: 6px; }
.pkg-sub  { font-size: 12px; color: #8392a5; }
.pkg-price { font-size: 14px; font-weight: 700; color: #0168fa; white-space: nowrap; }
.badge-popular {
  background: #0168fa;
  color: #fff;
  font-size: 9px;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
  letter-spacing: .03em;
}

/* ═══ Modal ═══ */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}
.modal-dialog {
  background: #fff;
  border-radius: 10px;
  width: 100%;
  max-width: 360px;
  margin: 16px;
  box-shadow: 0 20px 60px rgba(0,0,0,.2);
  overflow: hidden;
}
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  border-bottom: 1px solid #e5e9f2;
}
.modal-header h6 { font-size: 14px; font-weight: 600; margin: 0; }
.modal-close {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #8392a5;
  line-height: 1;
}
.modal-body { padding: 22px; }
.modal-icon-wrap {
  width: 56px; height: 56px;
  border-radius: 50%;
  background: #eef3ff;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 12px;
}
.modal-credits { font-size: 28px; font-weight: 700; color: #0168fa; text-align: center; line-height: 1; }
.modal-credits-sub { font-size: 13px; color: #8392a5; text-align: center; margin-bottom: 16px; }
.modal-table { width: 100%; font-size: 13px; border-collapse: collapse; }
.modal-table td { padding: 5px 0; color: #596882; }
.modal-table td:last-child { font-weight: 600; text-align: right; color: #1c273c; }
.price-cell { color: #0168fa !important; font-weight: 700 !important; }
.modal-table-divider td { border-top: 1px solid #f0f2f7; padding-top: 10px; }
.newbal-cell { color: #10b981 !important; }
.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 22px;
  border-top: 1px solid #e5e9f2;
}

/* ═══ Toast ═══ */
.toast-wrap {
  position: fixed;
  top: 20px; right: 20px;
  z-index: 9999;
  min-width: 280px;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 8px 30px rgba(0,0,0,.12);
  padding: 14px 18px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.toast-success { border-left: 4px solid #10b981; }
.toast-error   { border-left: 4px solid #ef4444; }
.toast-icon { font-size: 18px; flex-shrink: 0; }
.toast-success .toast-icon { color: #10b981; }
.toast-error   .toast-icon { color: #ef4444; }
.toast-text { flex: 1; }
.toast-title { font-weight: 600; font-size: 14px; }
.toast-msg   { font-size: 12px; color: #8392a5; }
.toast-close {
  background: none; border: none;
  cursor: pointer; color: #8392a5;
  font-size: 16px; padding: 0; line-height: 1;
}

/* Toast transition */
.toast-fade-enter-active,
.toast-fade-leave-active { transition: all .3s ease; }
.toast-fade-enter-from,
.toast-fade-leave-to { opacity: 0; transform: translateY(-8px); }

/* ═══ Responsive tweaks ═══ */
@media (max-width: 767px) {
  .wallet-hero { padding: 20px; }
  .hero-balance { font-size: 36px !important; }
}
</style>
